import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import { sqlDateToLabel, isoDateTimeToLabel, formatAmountValue } from 'helpers/helpers';
import { DatasetData } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';

import { Theme } from '@material-ui/core/styles/createTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Currency } from 'enums';

type StackingOptions = undefined | 'normal';

interface Props {
  data: ChartData[];
  title?: string;
  className?: string;
  loading?: boolean;
  currency?: Currency;
  translatedCurrency?: string;
  stacking: StackingOptions;
  allDataToTooltip?: ChartTooltipData | false; // show all data in tooltip
  labelType?: LabelType;
  legendOptions?: Highcharts.LegendOptions;
  xPlotLines?: Highcharts.XAxisPlotLinesOptions[];
  xAxisLabelFormatter?: Highcharts.FormatterCallbackFunction<Highcharts.AxisLabelsFormatterContextObject<number>>;
  yAxisLabelFormatter?: Highcharts.FormatterCallbackFunction<Highcharts.AxisLabelsFormatterContextObject<number>>;
  xAxisLabelStep?: number;
  callback?: Highcharts.ChartCallbackFunction;
  xAxisTitle?: string;
  setupLabels?: (visible: boolean) => void;
  toggleExtProduct?: (id: string, active: boolean) => void;
}

export enum LabelType {
  DATE = 'date',
  DATETIME = 'datetime',
}

export interface ChartData {
  name: string;
  type: 'area' | 'line';
  color?: string;
  lineColor?: string;
  data: string[][] | DatasetData;
  stacking?: StackingOptions;
  fillOpacity: number;
  id?: string;
  visible?: boolean;
}

export interface ChartTooltipData {
  [key: string]: {
    data: { [date: string]: any };
    detailData?: { [date: string]: any };
    color: string;
    title?: string;
  };
}

const ChartGraph: React.FC<Props> = ({
  data,
  className,
  title,
  stacking,
  currency,
  translatedCurrency,
  allDataToTooltip,
  labelType = LabelType.DATE,
  legendOptions = {
    align: 'left',
    layout: 'vertical',
    verticalAlign: 'middle',
    floating: false,
  },
  xPlotLines = [],
  xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
    const value = this.value as unknown as string;
    if (labelType === LabelType.DATE) {
      return sqlDateToLabel(value);
    } else if (labelType === LabelType.DATETIME) {
      return isoDateTimeToLabel(value);
    }
    return value;
  },
  yAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
    return this.value.toLocaleString('cs', {
      currency: currency,
      style: 'currency',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  },
  xAxisLabelStep,
  callback,
  xAxisTitle,
  setupLabels,
  toggleExtProduct
}) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();

  const getTooltipHeader = (tooltipData: Highcharts.TooltipFormatterContextObject): string => {
    if (!tooltipData || !tooltipData.points) return `<div></div>`;
    const date = tooltipData?.points[0].key as unknown as string; // usually date
    let value = '';

    switch (labelType) {
      case LabelType.DATE:
        value = sqlDateToLabel(date);
        break;
      case LabelType.DATETIME:
        value = isoDateTimeToLabel(date);
        break;
    }

    return `
      <div style="text-align: center; margin-bottom: 5px;">
        <strong>
          ${value}
        </strong>
      </div>
    `;
  };

  const getTooltipBody = (tooltipData: Highcharts.TooltipFormatterContextObject, smallMobile?: boolean): string => {
    if (!tooltipData || !tooltipData.points) return `<div></div>`;
    const { key: name } = tooltipData?.points[0];

    let s = '<table>';

    if (allDataToTooltip) {

      //console.log(allDataToTooltip);

      // add all charts values into tooltip
      for (const key in allDataToTooltip) {
        let dataSet = allDataToTooltip[key].data;
        const color = allDataToTooltip[key].color;
        const title = allDataToTooltip[key].title;
        let translatedKey = title ? title : color ? t(`${key}${smallMobile ? '.short' : ''}`) : t(key);
        let value = '';
        let style = '';

        if (key === 'client.age') {
          dataSet = allDataToTooltip[key].detailData || allDataToTooltip[key].data;
          value = dataSet && dataSet[name];
          const partnerIncluded = value.includes('/');
          if (partnerIncluded) {
            translatedKey += `/${t('general_partners')}`;
          }
          style = '_age';
        } else {
          value = `${dataSet && dataSet[name].toLocaleString(language)} ${translatedCurrency}`;
        }

        s += `
          <tr class="${style}">
            <td>${color ? `<span style="color: ${color};" class="_dot">\u25CF</span>` : ''}</td>
            <td>${translatedKey}</td>
            <td>${value}</td>
          </tr>
        `;
      }
    } else {
      s += `
        <tr>
          <td><span style="color: ${tooltipData.point.color};" class="_dot">\u25CF</span></td>
          <td>${tooltipData.series.name}</td>
          <td>${(tooltipData.y as number).toLocaleString(language)} ${translatedCurrency}</td>
        </tr>
      `;
    }

    s += '</table>';

    return s;
  };

  const tooltipFormatter = (tooltipData: Highcharts.TooltipFormatterContextObject, smallMobile?: boolean) => {
    let text = `<span>`;
    text += getTooltipHeader(tooltipData);
    text += getTooltipBody(tooltipData, smallMobile);
    text += `</span>`;
    return text;
  };

  const tablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), { noSsr: true });
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'), { noSsr: true });
  const smallMobile = useMediaQuery('@media (max-width: 400px)', { noSsr: true });

  const settings: Highcharts.Options = {
    chart: {
      spacingTop: 190,
      spacingRight: 35,
      height: 570,
      events: {
        redraw: function (this) {
          //const allVisible = this.series.every((s: Highcharts.Series) => s.visible === true);
          //setupLabels && setupLabels(allVisible);
          setupLabels && setupLabels(true);
        },

      },
    },
    title: {
      text: title || '',
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      area: {
        opacity: 1,
        stacking: stacking,
        lineWidth: 3,
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            enabled: false,
          },
          inactive: {
            opacity: 1,
          },
        },
      },
      series: {
        events: {
          hide: function (this) { toggleExtProduct && this.userOptions.id && toggleExtProduct(this.userOptions.id, false)  },
          show: function (this) { toggleExtProduct && this.userOptions.id && toggleExtProduct(this.userOptions.id, true)  },
        }
      }
    },
    tooltip: {
      useHTML: true,
      outside: false,
      shared: true,
      className: '_tooltip',

      formatter: function (this) {
        return tooltipFormatter(this);
      },

      positioner: function (labelWidth, labelHeight, point) {
        // Typescript definition problems: https://github.com/highcharts/highcharts/issues/13738
        const { plotX } = point as unknown as Highcharts.TooltipPositionerPointObject; // workaround of the issue
        const { plotSizeX, plotTop } = (this as any).chart; // there is no workaround for this, the TS definition is incompatible with implementation
        const tooltipPadding = 9;
        let xCoordinate = plotX - tooltipPadding * 2;
        xCoordinate = Math.max(xCoordinate, 0);

        // tooltip outside of plot area on right
        if (xCoordinate + labelWidth / 2 + tooltipPadding * 2 > plotSizeX) {
          xCoordinate = plotSizeX - labelWidth / 2 - tooltipPadding * 2 - 4;
        }

        return { x: xCoordinate, y: Math.max(plotTop - labelHeight - 5, 0) };
      },
    },

    xAxis: {
      tickInterval: xAxisLabelStep,
      labels: {
        enabled: true,
        formatter: xAxisLabelFormatter,
        align: 'center',
      },
      plotLines: xPlotLines,
      type: 'category', // making xLabels dates
      crosshair: {
        color: 'black',
        zIndex: 6,
        width: 1,
      },
      title: {
        text: xAxisTitle,
        align: 'high',
      },
    },
    yAxis: {
      offset: 10,
      labels: {
        align: 'right',
        formatter: yAxisLabelFormatter,
      },
      title: {
        text: '',
      },
    },
    legend: {

      labelFormatter: function () {
        return this.name;
      },
      ...legendOptions
    },
    series: data as any,

    responsive: {
      rules: [
        {
          condition: {
            callback: () => tablet,
          },
          chartOptions: {
            yAxis: {
              labels: {
                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
                  return formatAmountValue(this.value, currency, false, true);
                },
              },
            },
            legend: {
              align: 'center',
              itemMarginBottom: 10,
            },
          },
        },
        {
          condition: {
            maxWidth: 800,
          },
          chartOptions: {
            xAxis: {
              tickInterval: xAxisLabelStep ? xAxisLabelStep * 2 : undefined,
            },
          },
        },
        {
          condition: {
            callback: () => mobile,
          },
          chartOptions: {
            yAxis: {
              labels: {
                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
                  return formatAmountValue(this.value, null, false, true);
                },
              },
            },
            chart: {
              spacingTop: 60,
              marginRight: 0,
              spacingRight: 0,
              spacingLeft: 0,
            },
            tooltip: {
              positioner: function (labelWidth, labelHeight, point) {
                const { plotX } = point as unknown as Highcharts.TooltipPositionerPointObject; // workaround of the issue
                const { plotWidth, plotTop, plotLeft } = this.chart;
                let xCoordinate = plotX + plotLeft - labelWidth / 2;
                let yCoordinate = plotTop - labelHeight + 80; // fix for the tooltip position
                xCoordinate = Math.min(Math.max(xCoordinate, plotLeft), plotWidth + plotLeft - labelWidth);
                return { x: xCoordinate, y: yCoordinate };
              },
            },
          },
        },
        {
          condition: {
            callback: () => smallMobile,
          },
          chartOptions: {
            tooltip: {
              formatter: function (this) {
                return tooltipFormatter(this, true);
              },
              positioner: function (labelWidth, labelHeight, point) {
                const { plotTop } = (this as any).chart;
                return { x: 0, y: plotTop - labelHeight - 5 };
              },
            },
          },
        },
      ],
    },
  };

  Highcharts.setOptions({
    lang: {
      numericSymbols: [' tis.', ' mil.', ' mld.'],
    },
  });

  return <HighchartsReact highcharts={Highcharts} options={settings} className={`${className}`} callback={callback} />;
};

export default React.memo(ChartGraph);
